import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(null); // Store token in state instead of localStorage
  const navigate = useNavigate();





  // Check if already authenticated on mount
  useEffect(() => {
    // Simple check - just use localStorage like the original system
    const token = localStorage.getItem('accessToken');
    const username = localStorage.getItem('username');
    
    if (token && username) {
      const accessLevel = localStorage.getItem('access_level');
      const isSuperuser = localStorage.getItem('is_superuser') === 'true' || localStorage.getItem('is_superuser') === '1';
      const role = localStorage.getItem('role');
      
      setIsAuthenticated(true);
      setUser({
        username: localStorage.getItem('username'),
        role: role,
        accessLevel: accessLevel,
        isSuperuser: isSuperuser
      });
      setAuthToken(token);
    }
    
    setLoading(false);
  }, []);

  // Handle session expiration
  const handleSessionExpiration = () => {
    console.log('Session expired due to inactivity');
    
    // Clear all state and localStorage
    setAuthToken(null);
    setIsAuthenticated(false);
    setUser(null);
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('access_level');
    localStorage.removeItem('is_superuser');
    
    // Redirect to login
    navigate('/login');
  };



  // Simple login function - restore to original localStorage approach
  const login = (userData) => {
    // Store everything in localStorage like the original system
    localStorage.setItem('accessToken', userData.access);
    localStorage.setItem('username', userData.username);
    localStorage.setItem('role', userData.role);
    localStorage.setItem('access_level', userData.access_level);
    localStorage.setItem('is_superuser', userData.is_superuser);
    
    // Also store in state for React components
    setAuthToken(userData.access);
    setUser({
      username: userData.username,
      role: userData.role,
      accessLevel: userData.access_level,
      isSuperuser: userData.is_superuser
    });
    
    // Set authenticated
    setIsAuthenticated(true);
    
    // Navigate immediately
    navigate('/home');
  };

  // Logout function
  const logout = async () => {
    try {
      // Call backend logout endpoint to log the activity
      const token = localStorage.getItem('accessToken');
      if (token) {
        await fetch('/api/logout/', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      // Don't break logout if backend call fails
      console.error('Failed to log logout activity:', error);
    } finally {
      // Clear state
      setAuthToken(null);
      setIsAuthenticated(false);
      setUser(null);
      
      // Clear localStorage (like the original system)
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('username');
      localStorage.removeItem('role');
      localStorage.removeItem('access_level');
      localStorage.removeItem('is_superuser');
      
      navigate('/login');
    }
  };

  // Check if user has admin access - ONLY admin access level or superuser
  const isAdmin = () => {
    if (!user) return false;
    // Only allow superuser or users with specifically 'admin' access level
    return user.isSuperuser || user.role === 'admin';
  };

  // Check if user is superuser
  const isSuperuser = () => {
    if (!user) return false;
    return user.isSuperuser;
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    isAdmin,
    isSuperuser,
    getToken: () => authToken, // Function to get the current token
    handleSessionExpiration // Function to handle session expiration
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
